import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { CloseIcon, EyeIcon, EyeOffIcon, LoginIcon, UserIcon } from './Icons';
import './LoginModal.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Mode = 'search' | 'login' | 'register' | 'set-password';
type SearchMode = 'choice' | 'document' | 'phone';

type CustomerLookup = {
  id: string;
  cnpj: string;
  name: string;
  tradeName?: string | null;
  email?: string | null;
  phone?: string | null;
  cep?: string | null;
  address?: string | null;
  neighborhood?: string | null;
  number?: string | null;
  complement?: string | null;
  reference?: string | null;
  city?: string | null;
  state?: string | null;
};

type CepLookupResult = {
  cep?: string;
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  erro?: boolean;
};

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function maskDocument(value: string): string {
  const digits = onlyDigits(value).slice(0, 14);

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
  }

  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function maskPhone(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/^(\(\d{2}\) \d{4})(\d)/, '$1-$2');
  }

  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/^(\(\d{2}\) \d{5})(\d)/, '$1-$2');
}

function formatCep(value: string): string {
  const digits = onlyDigits(value).slice(0, 8);
  return digits.replace(/^(\d{5})(\d)/, '$1-$2');
}

export default function LoginModal({ open, onClose }: Props) {
  const { login } = useAuth();
  const [mode, setMode] = useState<Mode>('search');
  const [searchMode, setSearchMode] = useState<SearchMode>('choice');
  const [identifier, setIdentifier] = useState('');
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [documentValue, setDocumentValue] = useState('');
  const [name, setName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cep, setCep] = useState('');
  const [address, setAddress] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [number, setNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [reference, setReference] = useState('');
  const [city, setCity] = useState('');
  const [stateUf, setStateUf] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepMessage, setCepMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [customer, setCustomer] = useState<CustomerLookup | null>(null);

  const isLoginMode = mode === 'login';
  const isRegisterMode = mode === 'register';
  const isSetPasswordMode = mode === 'set-password';

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setMode('search');
    setSearchMode('choice');
    setIdentifier('');
    setLoginIdentifier('');
    setDocumentValue('');
    setName('');
    setTradeName('');
    setEmail('');
    setPhone('');
    setCep('');
    setAddress('');
    setNeighborhood('');
    setNumber('');
    setComplement('');
    setReference('');
    setCity('');
    setStateUf('');
    setPassword('');
    setError('');
    setMessage('');
    setCepMessage('');
    setCustomer(null);
    setShowPassword(false);
    setLoading(false);
    setCepLoading(false);
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'register') return;

    const digits = onlyDigits(cep);
    if (digits.length !== 8) return;

    const timeout = window.setTimeout(() => {
      void lookupCep(digits);
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [cep, mode, open]);

  if (!open) return null;

  function populateCustomerForm(data: CustomerLookup, fallbackIdentifier: string) {
    setCustomer(data);
    setDocumentValue(data.cnpj || fallbackIdentifier);
    setLoginIdentifier(data.cnpj || onlyDigits(fallbackIdentifier) || fallbackIdentifier);
    setName(data.name || '');
    setTradeName(data.tradeName || '');
    setEmail(data.email || '');
    setPhone(data.phone || '');
    setCep(data.cep || '');
    setAddress(data.address || '');
    setNeighborhood(data.neighborhood || '');
    setNumber(data.number || '');
    setComplement(data.complement || '');
    setReference(data.reference || '');
    setCity(data.city || '');
    setStateUf(data.state || '');
  }

  async function lookupCep(value: string) {
    const digits = onlyDigits(value);
    if (digits.length !== 8) return;

    setCepLoading(true);
    setCepMessage('');

    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = (await response.json()) as CepLookupResult;

      if (!response.ok || data.erro) {
        throw new Error('CEP nao encontrado');
      }

      setCep(formatCep(digits));
      setAddress(data.logradouro || '');
      setNeighborhood(data.bairro || '');
      setCity(data.localidade || '');
      setStateUf(data.uf || '');
      setCepMessage('Endereco carregado automaticamente pelo CEP');
    } catch (err) {
      setCepMessage(err instanceof Error ? err.message : 'Nao foi possivel buscar o CEP');
    } finally {
      setCepLoading(false);
    }
  }

  async function handleLookup(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const query = identifier.trim();
      if (!query) throw new Error('Digite CPF, CNPJ ou telefone');

      const result = await api.lookupCustomerAccess(query);

      if (result.status === 'not_found') {
        setMode('register');
        setMessage('Sem cadastro, deseja realizar agora?');
        if (searchMode === 'phone') {
          setDocumentValue('');
          setPhone(maskPhone(query));
          setLoginIdentifier(query);
        } else {
          setDocumentValue(maskDocument(query));
          setPhone('');
          setLoginIdentifier(query);
        }
        setName('');
        setTradeName('');
        setEmail('');
        setCep('');
        setAddress('');
        setNeighborhood('');
        setNumber('');
        setComplement('');
        setReference('');
        setCity('');
        setStateUf('');
        setCustomer(null);
        setPassword('');
        return;
      }

      if (result.customer) {
        populateCustomerForm(result.customer, query);
      }

      if (result.status === 'pending_password') {
        setMode('set-password');
        setMessage('Seu cadastro existe, mas a senha ainda nao foi criada.');
        setPassword('');
        return;
      }

      setMode('login');
      setMessage('Cadastro localizado. Digite sua senha para entrar.');
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nao foi possivel localizar o cliente');
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const doc = onlyDigits(documentValue);
      if (doc.length !== 11 && doc.length !== 14) {
        throw new Error('CPF ou CNPJ invalido');
      }
      if (!name.trim()) {
        throw new Error('Informe o nome do cliente');
      }
      const cepDigits = onlyDigits(cep);
      if (cepDigits.length !== 8) {
        throw new Error('CEP invalido');
      }
      if (!address.trim()) {
        throw new Error('Informe o endereco');
      }
      if (!neighborhood.trim()) {
        throw new Error('Informe o bairro');
      }
      if (!number.trim()) {
        throw new Error('Informe o numero');
      }
      if (password.trim().length < 6) {
        throw new Error('A senha precisa ter no minimo 6 caracteres');
      }

      await api.registerCustomerAccess({
        identifier: doc,
        name: name.trim(),
        password: password.trim(),
        tradeName: tradeName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        cep: cepDigits,
        address: address.trim(),
        neighborhood: neighborhood.trim(),
        number: number.trim(),
        complement: complement.trim() || null,
        reference: reference.trim() || null,
        city: city.trim() || null,
        state: stateUf.trim() || null,
      });

      await login(doc, password.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar cadastro');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreatePassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (password.trim().length < 6) {
        throw new Error('A senha precisa ter no minimo 6 caracteres');
      }

      const doc = customer?.cnpj || onlyDigits(documentValue) || onlyDigits(identifier);
      await api.setCustomerPassword({
        identifier: loginIdentifier || doc,
        password: password.trim(),
      });

      await login(loginIdentifier || doc, password.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar senha');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const doc = customer?.cnpj || onlyDigits(documentValue) || onlyDigits(identifier);
      const resolvedIdentifier = loginIdentifier || doc || customer?.cnpj || '';
      await login(resolvedIdentifier, password);
      setIdentifier('');
      setPassword('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  }

  function renderPrimaryForm() {
    if (mode === 'search' && searchMode === 'choice') {
      return (
        <div className="modal-search-choices">
          <button type="button" className="modal-search-choice" onClick={() => setSearchMode('document')}>
            <strong>Buscar por CPF/CNPJ</strong>
            <span>Se o cliente usa documento para acesso</span>
          </button>
          <button type="button" className="modal-search-choice" onClick={() => setSearchMode('phone')}>
            <strong>Buscar por telefone</strong>
            <span>Se o cliente acessa com número de celular</span>
          </button>
        </div>
      );
    }

    if (mode === 'search') {
      const isPhoneSearch = searchMode === 'phone';
      return (
        <form onSubmit={handleLookup}>
          <div className="modal-field">
            <label htmlFor="identifier">{isPhoneSearch ? 'Telefone' : 'CPF ou CNPJ'}</label>
            <div className="modal-input-wrap">
              <input
                id="identifier"
                type="text"
                inputMode="numeric"
                placeholder={isPhoneSearch ? '(00) 00000-0000' : '000.000.000-00 ou 00.000.000/0000-00'}
                value={identifier}
                onChange={e => setIdentifier(isPhoneSearch ? maskPhone(e.target.value) : maskDocument(e.target.value))}
                required
                autoFocus
              />
            </div>
          </div>

          {message && <div className="modal-guidance" role="note"><strong>{message}</strong></div>}
          {error && <p className="modal-error" role="alert">{error}</p>}

          <div className="modal-actions-row">
            <button type="button" className="modal-secondary" onClick={() => setSearchMode('choice')}>Voltar</button>
            <button type="submit" className="modal-submit" disabled={loading}>
              <LoginIcon size={18} />
              {loading ? 'Buscando...' : 'Continuar'}
            </button>
          </div>
        </form>
      );
    }

    if (isRegisterMode) {
      return (
        <form onSubmit={handleRegister}>
          <div className="modal-field">
            <label htmlFor="document">CPF ou CNPJ</label>
            <div className="modal-input-wrap">
              <input
                id="document"
                type="text"
                inputMode="numeric"
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                value={documentValue}
                onChange={e => setDocumentValue(maskDocument(e.target.value))}
                required
                autoFocus
              />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="name">Nome</label>
            <div className="modal-input-wrap">
              <input id="name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Nome ou razao social" required />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="cep">CEP</label>
            <div className="modal-input-wrap">
              <input id="cep" type="text" inputMode="numeric" value={cep} onChange={e => { setCep(formatCep(e.target.value)); setCepMessage(''); }} placeholder="00000-000" required />
              <button type="button" className="modal-secondary" onClick={() => void lookupCep(cep)} disabled={cepLoading}>
                {cepLoading ? 'Buscando...' : 'Buscar CEP'}
              </button>
            </div>
            {cepMessage && <p className="modal-hint">{cepMessage}</p>}
          </div>

          <div className="modal-field">
            <label htmlFor="address">Endereco</label>
            <div className="modal-input-wrap">
              <input id="address" type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Rua, avenida, lote, etc." required />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="neighborhood">Bairro</label>
            <div className="modal-input-wrap">
              <input id="neighborhood" type="text" value={neighborhood} onChange={e => setNeighborhood(e.target.value)} placeholder="Bairro" required />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="number">Numero</label>
            <div className="modal-input-wrap">
              <input id="number" type="text" value={number} onChange={e => setNumber(e.target.value)} placeholder="Numero" required />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="complement">Complemento</label>
            <div className="modal-input-wrap">
              <input id="complement" type="text" value={complement} onChange={e => setComplement(e.target.value)} placeholder="Opcional" />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="reference">Referencia</label>
            <div className="modal-input-wrap">
              <input id="reference" type="text" value={reference} onChange={e => setReference(e.target.value)} placeholder="Opcional" />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="city">Cidade</label>
            <div className="modal-input-wrap">
              <input id="city" type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="Opcional" />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="state">UF</label>
            <div className="modal-input-wrap">
              <input id="state" type="text" value={stateUf} onChange={e => setStateUf(e.target.value.toUpperCase().slice(0, 2))} placeholder="UF" maxLength={2} />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="tradeName">Nome fantasia</label>
            <div className="modal-input-wrap">
              <input id="tradeName" type="text" value={tradeName} onChange={e => setTradeName(e.target.value)} placeholder="Opcional" />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="phone">Telefone</label>
            <div className="modal-input-wrap">
              <input id="phone" type="text" value={phone} onChange={e => setPhone(maskPhone(e.target.value))} placeholder="Opcional" />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="email">Email</label>
            <div className="modal-input-wrap">
              <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Opcional" />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="password">Criar senha</label>
            <div className="modal-input-wrap">
              <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimo 6 caracteres" required />
              <button type="button" className="modal-password-toggle" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
              </button>
            </div>
          </div>

          {message && <p className="modal-guidance"><strong>{message}</strong></p>}
          {error && <p className="modal-error" role="alert">{error}</p>}

          <div className="modal-actions-row">
            <button type="button" className="modal-secondary" onClick={() => { setMode('search'); setSearchMode('choice'); }}>Voltar</button>
            <button type="submit" className="modal-submit" disabled={loading}>
              <LoginIcon size={18} />
              {loading ? 'Salvando...' : 'Criar cadastro'}
            </button>
          </div>
        </form>
      );
    }

    if (isSetPasswordMode) {
      return (
        <form onSubmit={handleCreatePassword}>
          <div className="modal-field">
            <label>Cliente localizado</label>
            <div className="modal-input-wrap">
              <input value={customer?.name || name} disabled />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="password">Criar senha</label>
            <div className="modal-input-wrap">
              <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimo 6 caracteres" required autoFocus />
              <button type="button" className="modal-password-toggle" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
              </button>
            </div>
          </div>

          {message && <p className="modal-guidance"><strong>{message}</strong></p>}
          {error && <p className="modal-error" role="alert">{error}</p>}

          <div className="modal-actions-row">
            <button type="button" className="modal-secondary" onClick={() => { setMode('search'); setSearchMode('choice'); }}>Voltar</button>
            <button type="submit" className="modal-submit" disabled={loading}>
              <LoginIcon size={18} />
              {loading ? 'Criando...' : 'Criar senha'}
            </button>
          </div>
        </form>
      );
    }

    if (isLoginMode) {
      return (
        <form onSubmit={handleLogin}>
          <div className="modal-field">
            <label>Cliente localizado</label>
            <div className="modal-input-wrap">
              <input value={customer?.name || name || identifier} disabled />
            </div>
          </div>

          <div className="modal-field">
            <label htmlFor="password">Senha</label>
            <div className="modal-input-wrap">
              <input id="password" type={showPassword ? 'text' : 'password'} placeholder="Sua senha" value={password} onChange={e => setPassword(e.target.value)} required autoFocus />
              <button type="button" className="modal-password-toggle" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
              </button>
            </div>
          </div>

          {message && <p className="modal-guidance"><strong>{message}</strong></p>}
          {error && <p className="modal-error" role="alert">{error}</p>}

          <div className="modal-actions-row">
            <button type="button" className="modal-secondary" onClick={() => { setMode('search'); setSearchMode('choice'); }}>Voltar</button>
            <button type="submit" className="modal-submit" disabled={loading}>
              <LoginIcon size={18} />
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </div>
        </form>
      );
    }

  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content surface-card" onClick={e => e.stopPropagation()}>
        <div className="modal-hero">
          <span className="modal-hero-badge">
            <UserIcon size={16} />
            Acesso do cliente
          </span>
          <button className="modal-close" onClick={onClose} aria-label="Fechar" type="button">
            <CloseIcon size={20} />
          </button>
          <h2>Finalizar pedido</h2>
          <p className="modal-subtitle">Digite CPF, CNPJ ou telefone. Se nao existir cadastro, voce cria na hora.</p>
        </div>

        {renderPrimaryForm()}

        {import.meta.env.VITE_DEMO_CNPJ && (
          <p className="modal-hint">
            Credenciais de demonstracao: CNPJ <strong>{import.meta.env.VITE_DEMO_CNPJ}</strong>
            {import.meta.env.VITE_DEMO_PASSWORD && <> , senha <strong>{import.meta.env.VITE_DEMO_PASSWORD}</strong></>}
          </p>
        )}
      </div>
    </div>
  );
}
